/**
 * The channel between a writer and the change-feed poller of its tab
 * (2026-11-03).
 *
 * WHY IT EXISTS. A writer re-reads what it wrote as soon as the write answers,
 * which its own screen is waiting on. The poller then reports the same write
 * on its next page, a second or so later, and re-read it all again: on the
 * Tasks board that was eight register reads for one tick where four were
 * enough, each one a render of the whole board.
 *
 * WHAT IT DOES. The writer asks for one poll and names the query roots it is
 * about to re-read. The poller polls at once (or as soon as the poll already
 * out comes back, since that one may have started before the write), applies
 * the page with those roots left out, and answers. The writer then re-reads.
 * The re-read starts after the page came back, so it sees every change that
 * page names, another user's included; a root the page needed and the writer
 * did not name is applied as any page applies it. A page this tab did not ask
 * for (another user's write, another tab's) is untouched by any of this.
 *
 * WHY NOT SKIP BY MUTATION ID. A page carries table names only, never rows,
 * actors or mutation ids (spec ruling 17), so a tab cannot tell its own change
 * from someone else's on the same table. Ordering the writer's re-read after
 * the page is what makes leaving the roots out exact.
 *
 * The answer is `true` when the page was applied with the roots left out, and
 * `false` when no poll could do that (no poller for this client, a hidden tab,
 * a failed or resync page, the writer stopped waiting). Either way the writer
 * re-reads; `false` only means the feed may report the write again.
 */
import type { QueryRoot } from './types'

/** A poller's side of the channel: poll for a writer that will re-read `roots`. */
export type OwnWritePoll = (roots: ReadonlyArray<QueryRoot>, signal?: AbortSignal) => Promise<boolean>

const pollers = new WeakMap<object, OwnWritePoll>()

/** Called by the poller of `client` while it runs; the returned function stops serving. */
export const serveOwnWritePolls = (client: object, poll: OwnWritePoll): (() => void) => {
  pollers.set(client, poll)
  return () => {
    if (pollers.get(client) === poll) pollers.delete(client)
  }
}

/**
 * Ask the poller of `client` for one poll that leaves `roots` to the caller,
 * and wait for it to be applied. Resolves `false` at once when nothing serves
 * this client, and when `signal` aborts, after which the page is applied whole.
 */
export const requestOwnWritePoll = (
  client: object,
  roots: ReadonlyArray<QueryRoot>,
  signal?: AbortSignal,
): Promise<boolean> => {
  const poll = pollers.get(client)
  return poll === undefined ? Promise.resolve(false) : poll(roots, signal)
}
